import { sanitizeAadhaarDigits } from '@shared/directives/aadhaar-format.util';

/** Entity-type letters allowed at PAN position 4 for non-individual / corporate applicants. */
export const CORPORATE_PAN_ENTITY_CHARS = 'CFATBLJG';

/** @deprecated Use {@link CORPORATE_PAN_ENTITY_CHARS} — same set for corporate opportunity flows. */
export const COMPANY_PAN_ENTITY_CHARS = CORPORATE_PAN_ENTITY_CHARS;

export const CORPORATE_PAN_PATTERN = new RegExp(
  `^[A-Z]{3}[${CORPORATE_PAN_ENTITY_CHARS}][A-Z]\\d{4}[A-Z]$`,
);
export const CORPORATE_PAN_PATTERN_SOURCE = `^[A-Z]{3}[${CORPORATE_PAN_ENTITY_CHARS}][A-Z][0-9]{4}[A-Z]$`;

/** Corporate / non-individual PAN validation (all entity 4th-character types). */
export const COMPANY_PAN_PATTERN = CORPORATE_PAN_PATTERN;
export const COMPANY_PAN_PATTERN_SOURCE = CORPORATE_PAN_PATTERN_SOURCE;

/** Indian individual PAN: 4th character must be P (e.g. ABCPA1234F). */
export const INDIVIDUAL_PAN_PATTERN = /^[A-Z]{3}P[A-Z]\d{4}[A-Z]$/;
export const INDIVIDUAL_PAN_PATTERN_SOURCE = '^[A-Z]{3}P[A-Z][0-9]{4}[A-Z]$';

export function corporatePanValidationHint(): string {
  return (
    'Format: 5 letters + 4 digits + 1 letter; 4th character must be one of: ' +
    'C (Company), F (Firm/LLP), A (AOP), T (Trust), B (BOI), L (Local Authority), ' +
    'J (Artificial Juridical Person), G (Government) — e.g. AAACA1234D.'
  );
}

export function individualPanValidationHint(): string {
  return 'Format: 5 letters + 4 digits + 1 letter; 4th character must be P (e.g., ABCPA1234F).';
}

function isPanLetter(char: string): boolean {
  return /[A-Z]/.test(char);
}

function isPanDigit(char: string): boolean {
  return /\d/.test(char);
}

function isPanEntityChar(
  char: string,
  isCorporate: boolean,
  entityChars?: string,
): boolean {
  if (!isCorporate) {
    return char === 'P';
  }
  const allowed = entityChars ?? CORPORATE_PAN_ENTITY_CHARS;
  return allowed.includes(char);
}

function acceptsPanCharAt(
  char: string,
  position: number,
  isCorporate: boolean,
  entityChars?: string,
): boolean {
  if (position <= 2 || position === 4 || position === 9) {
    return isPanLetter(char);
  }
  if (position === 3) {
    return isPanEntityChar(char, isCorporate, entityChars);
  }
  return position >= 5 && position <= 8 && isPanDigit(char);
}

/** Mask PAN input position-by-position for individual (4th char P) or corporate/company PAN. */
export function maskPanInput(
  raw: string,
  isCorporate: boolean,
  entityChars?: string,
): string {
  const chars = raw.replaceAll(/[^A-Za-z0-9]/g, '').toUpperCase();
  let result = '';

  for (const char of chars) {
    if (result.length >= 10) {
      break;
    }
    if (acceptsPanCharAt(char, result.length, isCorporate, entityChars)) {
      result += char;
    }
  }

  return result;
}

/** Uppercase alphanumeric only, max 10 — used for existing-customer search PAN entry. */
export function maskPanInputRelaxed(raw: string): string {
  return raw.replaceAll(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 10);
}

/** Allow digits only, max 12 characters (display grouping: use `app-input-field` `[aadhaarFormat]="true"`). */
export function maskAadhaarInput(raw: string): string {
  return sanitizeAadhaarDigits(raw);
}

/** Indian mobile: digits only, max 10, first digit 6–9. */
export function maskIndianMobileInput(raw: string): string {
  const digits = raw.replaceAll(/\D/g, '');
  let result = '';
  for (const digit of digits) {
    if (result.length >= 10) {
      break;
    }
    if (result.length === 0 && !/[6-9]/.test(digit)) {
      continue;
    }
    result += digit;
  }
  return result;
}
